import {
  type InfiniteScrollCustomEvent,
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  type RefresherEventDetail,
} from '@ionic/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCompany, useModuleLevel } from '~/api/hooks/useCompany';
import { type InfiniteInvoicesFilters, useInfiniteInvoicesQuery } from '~/api/hooks/useInvoices';
import { INVOICE_STATUS_LABEL, type InvoiceListItem, type InvoiceStatus } from '~/api/invoices';
import { Icon } from '~/components/ui/Icon';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { splitBrl } from '~/lib/format';
import { ROUTES, notaPath } from '~/routes';
import { ACCESS_LEVEL, atLeast } from '~/session/access';

interface StatusChip {
  value: InvoiceStatus | 'ALL';
  label: string;
}

const STATUS_CHIPS: readonly StatusChip[] = [
  { value: 'ALL', label: 'Tudo' },
  { value: 'AUTHORIZED', label: 'Autorizadas' },
  { value: 'PROCESSING', label: 'Processando' },
  { value: 'ERROR', label: 'Erro' },
  { value: 'CANCELLED', label: 'Canceladas' },
  { value: 'DRAFT', label: 'Rascunhos' },
];

const STATUS_TONE: Record<InvoiceStatus, { dot: string; tagClass: string }> = {
  AUTHORIZED: { dot: 'var(--rx-positive)', tagClass: 'rx-tag-pos' },
  PROCESSING: { dot: 'var(--rx-warn)', tagClass: 'rx-tag-warn' },
  DRAFT: { dot: 'var(--rx-text-3)', tagClass: 'rx-tag' },
  ERROR: { dot: 'var(--rx-negative)', tagClass: 'rx-tag-neg' },
  REJECTED: { dot: 'var(--rx-negative)', tagClass: 'rx-tag-neg' },
  CANCELLED: { dot: 'var(--rx-text-2)', tagClass: 'rx-tag' },
};

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  month: 'long',
  year: 'numeric',
});

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
});

function currentYearMonth(): string {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
}

function buildMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let offset = 0; offset < 12; offset += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const value = `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    options.push({ value, label: MONTH_LABEL_FORMATTER.format(date) });
  }
  return options;
}

function groupKey(invoice: InvoiceListItem): string {
  const iso = invoice.data_emissao ?? invoice.created_at;
  return iso.slice(0, 10);
}

function groupHeading(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const short = SHORT_DATE_FORMATTER.format(date).replace('.', '');
  if (sameDay(date, today)) return `Hoje · ${short}`;
  if (sameDay(date, yesterday)) return `Ontem · ${short}`;
  return short.toUpperCase();
}

export function Notas(): JSX.Element {
  const nav = useAppNavigation();
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'ALL'>('ALL');
  const [mesAno, setMesAno] = useState<string>('ALL');
  const monthOptions = useMemo(() => buildMonthOptions(), []);

  const filters: InfiniteInvoicesFilters = useMemo(() => {
    const result: InfiniteInvoicesFilters = {};
    if (statusFilter !== 'ALL') result.status = statusFilter;
    if (mesAno !== 'ALL') result.mesAno = mesAno;
    return result;
  }, [statusFilter, mesAno]);

  const { selected: company } = useCompany();
  const canIssue = atLeast(useModuleLevel('nfse'), ACCESS_LEVEL.full);
  const query = useInfiniteInvoicesQuery(company?.id, filters);

  const allInvoices: InvoiceListItem[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.results) ?? [],
    [query.data],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, InvoiceListItem[]>();
    for (const inv of allInvoices) {
      const key = groupKey(inv);
      const arr = map.get(key) ?? [];
      arr.push(inv);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [allInvoices]);

  const totals = useMemo(() => {
    let autorizadas = 0;
    let pendentes = 0;
    let autorizadasValue = 0;
    let pendentesValue = 0;
    for (const inv of allInvoices) {
      const value = Number(inv.valor_servico) || 0;
      if (inv.status === 'AUTHORIZED') {
        autorizadas += 1;
        autorizadasValue += value;
      }
      if (inv.status === 'PROCESSING' || inv.status === 'DRAFT') {
        pendentes += 1;
        pendentesValue += value;
      }
    }
    return { autorizadas, pendentes, autorizadasValue, pendentesValue };
  }, [allInvoices]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>): Promise<void> => {
    await query.refetch();
    event.detail.complete();
  };

  const handleInfinite = async (event: InfiniteScrollCustomEvent): Promise<void> => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      await query.fetchNextPage();
    }
    await (event.target as HTMLIonInfiniteScrollElement).complete();
  };

  const isEmpty = !query.isPending && allInvoices.length === 0;
  const hasFilter = statusFilter !== 'ALL' || mesAno !== 'ALL';

  return (
    <IonPage>
      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="rx-container rx-screen" style={{ paddingBottom: 28 }}>
          <div style={{ padding: '12px 24px 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <button
                type="button"
                className="rx-iconbtn"
                onClick={() => nav.replace(ROUTES.dashboard)}
                aria-label="Voltar"
              >
                <Icon name="back" size={18} />
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="rx-iconbtn" aria-label="Buscar">
                  <Icon name="search" size={18} />
                </button>
                {canIssue && (
                  <Link to={ROUTES.emitir} className="rx-iconbtn" aria-label="Emitir nova">
                    <Icon name="plus" size={18} />
                  </Link>
                )}
              </div>
            </div>

            <h1
              style={{
                margin: '12px 0 0',
                fontSize: 30,
                fontWeight: 500,
                color: 'var(--rx-text)',
                letterSpacing: '-0.7px',
              }}
            >
              Minhas notas
            </h1>
            <p style={{ marginTop: 4, fontSize: 13, color: 'var(--rx-text-2)' }}>
              Histórico fiscal · acompanhe status e baixe PDFs
            </p>
          </div>

          {/* Filtro de status */}
          <div
            style={{
              marginTop: 14,
              padding: '0 24px',
              display: 'flex',
              gap: 6,
              overflowX: 'auto',
            }}
          >
            {STATUS_CHIPS.map((chip) => {
              const active = chip.value === statusFilter;
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => setStatusFilter(chip.value)}
                  className={`rx-chip ${active ? 'rx-chip-active' : ''}`}
                  style={{ flexShrink: 0, cursor: 'pointer' }}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>

          {/* Período + summary */}
          <div style={{ padding: '14px 24px 0', display: 'flex', gap: 10 }}>
            <SummaryTile
              label="Autorizadas"
              count={totals.autorizadas}
              value={totals.autorizadasValue}
              tone="positive"
            />
            <SummaryTile
              label="Pendentes"
              count={totals.pendentes}
              value={totals.pendentesValue}
              tone="muted"
            />
          </div>

          {/* Seletor de mês */}
          <div style={{ padding: '14px 24px 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: 'var(--rx-surface)',
                border: '0.5px solid var(--rx-border)',
                borderRadius: 12,
              }}
            >
              <Icon name="filter" size={14} color="var(--rx-text-3)" />
              <select
                value={mesAno}
                onChange={(event) => setMesAno(event.target.value)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  color: 'var(--rx-text)',
                  appearance: 'none',
                }}
              >
                <option value="ALL">Todos os meses</option>
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                    {option.value === currentYearMonth() ? ' (atual)' : ''}
                  </option>
                ))}
              </select>
              <Icon name="chevron" size={14} color="var(--rx-text-3)" />
            </div>
          </div>

          {/* Lista */}
          {query.isPending ? (
            <div style={{ padding: '16px 24px 0' }}>
              <div className="rx-card rx-card-flush">
                <ListSkeleton />
              </div>
            </div>
          ) : query.isError ? (
            <ListError message={query.error.message} onRetry={() => query.refetch()} />
          ) : isEmpty ? (
            <EmptyState hasFilter={hasFilter} canIssue={canIssue} />
          ) : (
            <div style={{ padding: '8px 24px 0' }}>
              {grouped.map(([date, items]) => (
                <div key={date} style={{ marginTop: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 4px 8px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--rx-text-3)',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {groupHeading(date)}
                    </span>
                    <span
                      className="rx-mono"
                      style={{ fontSize: 11, fontWeight: 500, color: 'var(--rx-text-2)' }}
                    >
                      {items.length.toString().padStart(2, '0')} nota
                      {items.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="rx-card rx-card-flush">
                    {items.map((nota, idx) => (
                      <NotaRow key={nota.id} nota={nota} first={idx === 0} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <IonInfiniteScroll
            onIonInfinite={handleInfinite}
            disabled={!query.hasNextPage}
            threshold="200px"
          >
            <IonInfiniteScrollContent
              loadingSpinner="dots"
              loadingText="Carregando mais notas..."
            />
          </IonInfiniteScroll>

          {!query.hasNextPage && allInvoices.length > 0 && (
            <div
              style={{
                marginTop: 24,
                padding: '0 24px',
                textAlign: 'center',
              }}
            >
              <div
                className="rx-mono"
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase',
                  color: 'var(--rx-text-3)',
                }}
              >
                Fim · {allInvoices.length} nota{allInvoices.length === 1 ? '' : 's'}
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

interface SummaryTileProps {
  label: string;
  count: number;
  value: number;
  tone: 'positive' | 'muted';
}

const BRL = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function SummaryTile({ label, count, value, tone }: SummaryTileProps): JSX.Element {
  const bg = tone === 'positive' ? 'var(--rx-positive-tint)' : 'var(--rx-surface-alt)';
  const labelColor = tone === 'positive' ? 'var(--rx-positive)' : 'var(--rx-text-2)';
  return (
    <div
      style={{
        flex: 1,
        padding: '12px 14px',
        borderRadius: 12,
        background: bg,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: labelColor,
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
        }}
      >
        {label} · {count.toString().padStart(2, '0')}
      </div>
      <div
        className="rx-mono"
        style={{
          marginTop: 6,
          fontSize: 18,
          fontWeight: 500,
          color: 'var(--rx-text)',
          letterSpacing: '-0.4px',
        }}
      >
        R$ {BRL.format(value)}
      </div>
    </div>
  );
}

interface NotaRowProps {
  nota: InvoiceListItem;
  first: boolean;
}

function NotaRow({ nota, first }: NotaRowProps): JSX.Element {
  const iso = nota.data_emissao ?? nota.created_at;
  const date = new Date(iso);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase();
  const tone = STATUS_TONE[nota.status];
  const { integer, cents } = splitBrl(nota.valor_servico);

  return (
    <Link
      to={notaPath(nota.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderTop: first ? 'none' : '0.5px solid var(--rx-border)',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          flexShrink: 0,
          background: 'var(--rx-surface-alt)',
          border: '0.5px solid var(--rx-border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          className="rx-mono"
          style={{ fontSize: 10, fontWeight: 600, color: 'var(--rx-text-3)' }}
        >
          {month}
        </div>
        <div
          className="rx-mono"
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--rx-text)',
            lineHeight: 1,
            letterSpacing: '-0.3px',
          }}
        >
          {day}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 6,
              background: tone.dot,
              flexShrink: 0,
            }}
            aria-hidden
          />
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--rx-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nota.tomador_razao_social || nota.tomador_cpf_cnpj || 'Sem tomador'}
          </span>
        </div>
        <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className={`rx-tag ${tone.tagClass}`}>{INVOICE_STATUS_LABEL[nota.status]}</span>
          {nota.numero != null && (
            <span className="rx-mono" style={{ fontSize: 10, color: 'var(--rx-text-3)' }}>
              Nº {nota.numero}
            </span>
          )}
        </div>
      </div>
      <div
        className="rx-mono"
        style={{
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: '-0.1px',
          color: 'var(--rx-text)',
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        R$ {integer}
        <span style={{ color: 'var(--rx-text-3)' }}>,{cents}</span>
      </div>
    </Link>
  );
}

function ListSkeleton(): JSX.Element {
  return (
    <>
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          style={{
            padding: '14px 16px',
            borderTop: index === 0 ? 'none' : '0.5px solid var(--rx-border)',
          }}
        >
          <IonSkeletonText animated style={{ width: '60%', height: 14 }} />
          <IonSkeletonText animated style={{ width: '40%', height: 11, marginTop: 6 }} />
        </div>
      ))}
    </>
  );
}

function ListError({ message, onRetry }: { message: string; onRetry(): void }): JSX.Element {
  return (
    <div style={{ padding: '24px 24px 0', textAlign: 'center' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: 22,
          background: 'var(--rx-negative-tint)',
          color: 'var(--rx-negative)',
        }}
      >
        <Icon name="warn" size={20} />
      </div>
      <p
        style={{
          marginTop: 12,
          fontSize: 14,
          color: 'var(--rx-text)',
          maxWidth: '36ch',
          marginInline: 'auto',
        }}
      >
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="rx-mono"
        style={{
          marginTop: 12,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: 'var(--rx-brand)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Tentar novamente
      </button>
    </div>
  );
}

function EmptyState({
  hasFilter,
  canIssue,
}: { hasFilter: boolean; canIssue: boolean }): JSX.Element {
  return (
    <div style={{ padding: '32px 24px 0', textAlign: 'center' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 52,
          height: 52,
          borderRadius: 26,
          background: 'var(--rx-brand-tint)',
          color: 'var(--rx-brand)',
        }}
      >
        <Icon name={hasFilter ? 'search' : 'doc'} size={22} />
      </div>
      <p
        style={{
          marginTop: 14,
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--rx-text-3)',
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
        }}
      >
        {hasFilter ? 'Nada por aqui' : 'Nenhuma nota ainda'}
      </p>
      <h2
        style={{
          marginTop: 8,
          fontSize: 22,
          fontWeight: 500,
          color: 'var(--rx-text)',
          letterSpacing: '-0.4px',
        }}
      >
        {hasFilter ? 'Sem resultados para o filtro.' : 'Comece pela primeira emissão.'}
      </h2>
      <p
        style={{
          marginTop: 8,
          fontSize: 13,
          color: 'var(--rx-text-2)',
          maxWidth: '32ch',
          marginInline: 'auto',
        }}
      >
        {hasFilter
          ? 'Tente ampliar o período ou trocar o status.'
          : canIssue
            ? 'Acompanhe status, baixe PDF e cancele tudo daqui.'
            : 'As notas emitidas para esta empresa aparecem aqui.'}
      </p>
      {!hasFilter && canIssue && (
        <Link
          to={ROUTES.emitir}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 16,
            padding: '10px 18px',
            borderRadius: 14,
            background: 'var(--rx-brand)',
            color: 'var(--rx-brand-ink)',
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: '-0.2px',
            textDecoration: 'none',
          }}
        >
          <Icon name="flash" size={16} />
          Emitir NFSe
        </Link>
      )}
    </div>
  );
}
